/**
 * Knowledge Base API
 */

import apiClient from './client';

// 데이터 소스 유형
export type SourceType = 'file' | 'web' | 'database' | 'drive';

// 청킹 전략 (AWS Bedrock 청킹 전략 대응)
export type ChunkingStrategy = 'default' | 'fixed' | 'paragraph' | 'semantic' | 'hierarchical' | 'none';

// 전처리 설정
export interface PreprocessingConfig {
  // 파일 전용
  enableOcr: boolean;
  removeHeaders: boolean;
  removeFooters: boolean;
  extractTables: boolean;
  cleanWhitespace: boolean;
  // 웹 크롤링 전용 (웹 표준 전처리)
  removeBoilerplate?: boolean;   // nav/header/footer/aside 등 보일러플레이트 제거
  mainContentOnly?: boolean;     // main/article 본문 영역 우선 추출
  removeAds?: boolean;           // 광고·추적 요소 제거
  // DB 전용
  dbNullHandling: boolean;        // NULL 값 처리 (제외 또는 "없음" 표시)
  dbDateFormat: boolean;          // 날짜/시간 포맷 정규화
  dbNumberFormat: boolean;        // 숫자 포맷 정규화 (천단위 구분, 단위 추가)
  dbDeduplication: boolean;       // 중복 레코드 제거
  dbTextColumnsOnly: boolean;     // 긴 텍스트 컬럼만 추출 (코드성 짧은 컬럼 제외)
  dbMaskSensitive: boolean;       // 민감 정보 마스킹 (이메일·전화번호·주민번호)
}

// 청킹 설정
export interface ChunkingConfig {
  strategy: ChunkingStrategy;
  chunkSize: number;
  chunkOverlap: number;
  respectSentenceBoundary: boolean;
  // 계층형(hierarchical) 전략 전용 파라미터
  parentChunkSize?: number;
  childChunkSize?: number;
}

// 검색 유형
export type SearchType = 'semantic' | 'hybrid';

// 검색 설정
export interface SearchConfig {
  topK: number;
  similarityThreshold: number;
  enableReranking: boolean;
  /** 검색 유형: semantic(벡터만, 0~1 점수), hybrid(벡터+텍스트 BM25) */
  searchType?: SearchType;
}

// 파이프라인 설정
export interface PipelineConfig {
  sourceType: SourceType;
  preprocessing: PreprocessingConfig;
  chunking: ChunkingConfig;
  embeddingModelId: string;
  search: SearchConfig;
}

// 임베딩 모델 옵션
export interface EmbeddingModelOption {
  id: string;
  name: string;
  provider: string;
  dimensions: number;
  maxTokens: number;
  costPer1kTokens: number;
}

export interface KnowledgeBase {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'syncing' | 'indexing' | 'error' | 'needs-sync';
  documentCount: number;
  embeddingModel: string;
  chunkSize: number;
  chunkOverlap: number;
  sourceType: SourceType;
  owner: string;
  ownerName?: string;  // 소유자 이름 (관리자 조회 시 제공)
  tenantId?: string;   // 소속 테넌트 ID
  customerId?: string; // 소속 고객사 ID
  lastSyncedAt?: string;
  pipelineConfig?: PipelineConfig;
  errorMessage?: string;  // 에러 메시지
  createdAt: string;
  updatedAt: string;
}

export interface Document {
  id: string;
  knowledgeBaseId: string;
  name: string;
  type: 'pdf' | 'txt' | 'md';
  size: number;
  status: 'pending' | 'processing' | 'indexed' | 'error';
  s3Key: string;
  chunkCount?: number;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SearchResult {
  documentId: string;
  documentName: string;
  content: string;
  score: number;
  chunkIndex?: number;
}

export interface CreateKnowledgeBaseRequest {
  name: string;
  description?: string;
  sourceType?: SourceType;
  chunkSize?: number;
  chunkOverlap?: number;
  pipelineConfig?: Partial<PipelineConfig>;
}

// 사용 가능한 임베딩 모델 목록 (AWS Bedrock 실제 가격 기준 - 2024.08)
// https://aws.amazon.com/bedrock/pricing/
// Titan V2와 Cohere Embed Multilingual v3 모두 1024차원이라 인덱스 구조가 호환된다.
// (V1은 구형·1536차원이라 제외) 모델별로 인덱스를 분리 색인하며, 검색도 모델에 맞는
// 인덱스에서 수행한다. Cohere는 멀티모달(이미지) 확장 여지가 있어 파일 소스에 적합하다.
export const EMBEDDING_MODELS: EmbeddingModelOption[] = [
  {
    id: 'amazon.titan-embed-text-v2:0',
    name: 'Titan Text Embeddings V2',
    provider: 'Amazon Bedrock',
    dimensions: 1024,
    maxTokens: 8192,
    costPer1kTokens: 0.00002, // $0.02 per 1M input tokens = $0.00002 per 1K
  },
  {
    id: 'cohere.embed-multilingual-v3',
    name: 'Cohere Embed Multilingual V3',
    provider: 'Cohere (Bedrock)',
    dimensions: 1024,
    maxTokens: 512,
    costPer1kTokens: 0.0001, // $0.10 per 1M input tokens = $0.0001 per 1K
  },
];

export const knowledgeBaseApi = {
  async list(options?: { includeSystem?: boolean }) {
    const params = new URLSearchParams();
    if (options?.includeSystem) {
      params.set('includeSystem', 'true');
    }
    const url = params.toString() ? `/api/knowledge-bases?${params}` : '/api/knowledge-bases';
    const response = await apiClient.get<{ success: boolean; data: KnowledgeBase[] }>(url);
    return response.data.data;
  },

  async get(id: string) {
    const response = await apiClient.get<{ success: boolean; data: KnowledgeBase }>(`/api/knowledge-bases/${id}`);
    return response.data.data;
  },

  async create(data: CreateKnowledgeBaseRequest & { customerIdOverride?: string; teamIdOverride?: string }) {
    const response = await apiClient.post<{ success: boolean; data: KnowledgeBase }>('/api/knowledge-bases', data);
    return response.data.data;
  },

  async delete(id: string): Promise<{ affectedWorkflows?: string[] }> {
    const response = await apiClient.delete<{ 
      success: boolean; 
      affectedWorkflows?: string[];
    }>(`/api/knowledge-bases/${id}`);
    return { affectedWorkflows: response.data.affectedWorkflows };
  },

  async update(id: string, data: { 
    name?: string; 
    description?: string; 
    owner?: string;
    pipelineConfig?: Record<string, unknown>;
    customerIdOverride?: string;
    teamIdOverride?: string;
  }) {
    const response = await apiClient.put<{ success: boolean; data: KnowledgeBase }>(
      `/api/knowledge-bases/${id}`,
      data
    );
    return response.data.data;
  },

  async getDocuments(kbId: string, limit = 100) {
    const response = await apiClient.get<{ success: boolean; data: Document[] }>(`/api/knowledge-bases/${kbId}/documents?limit=${limit}`);
    return response.data.data;
  },

  async uploadDocument(kbId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post<{ success: boolean; data: Document }>(
      `/api/knowledge-bases/${kbId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  async deleteDocument(kbId: string, docId: string) {
    await apiClient.delete(`/api/knowledge-bases/${kbId}/documents/${docId}`);
  },

  async reprocessDocument(kbId: string, docId: string) {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/api/knowledge-bases/${kbId}/documents/${docId}/reprocess`
    );
    return response.data;
  },

  // 동기화/재크롤링 실행
  async sync(kbId: string) {
    const response = await apiClient.post<{ success: boolean; data: KnowledgeBase }>(
      `/api/knowledge-bases/${kbId}/sync`
    );
    return response.data.data;
  },

  async search(kbId: string, query: string) {
    // topK, threshold 등은 백엔드에서 지식베이스 설정을 읽어서 적용
    const response = await apiClient.post<{ 
      success: boolean; 
      data: { results: SearchResult[]; query: string; processingTimeMs: number } 
    }>(`/api/knowledge-bases/${kbId}/search`, { query });
    return response.data.data;
  },

  // ── DB 커넥터 ──

  /** DB 연결 테스트 + 테이블/컬럼 목록 조회 */
  async dbTest(params: {
    type: string;
    host: string;
    port: number;
    database: string;
    schema?: string;
    username: string;
    password?: string;
  }) {
    const response = await apiClient.post<{
      success: boolean;
      tables: Array<{
        schema: string;
        table: string;
        columns: Array<{
          name: string;
          type: string;
          nullable: boolean;
          comment: string;
          label: string;
          include: boolean;
        }>;
      }>;
      error?: string;
    }>('/api/knowledge-bases/db/test', params);
    return response.data;
  },

  /** LLM 컬럼 한글 설명 자동 생성 */
  async dbGenerateLabels(params: {
    columns: Array<{ name: string; type: string; comment: string }>;
  }) {
    const response = await apiClient.post<{
      success: boolean;
      labels: string[];
    }>('/api/knowledge-bases/db/generate-labels', params);
    return response.data;
  },

  /**
   * Text-to-SQL 실행 — fetch 기반 SSE 스트리밍 헬퍼
   * async generator로 이벤트를 순서대로 반환합니다.
   */
  async *dbQueryStream(params: {
    knowledgeBaseId: string;
    query: string;
    connection: {
      type: 'mysql' | 'postgresql' | 'redshift' | 'oracle' | 'mssql';
      host: string;
      port: number;
      database: string;
      schema?: string;
      username: string;
      password?: string;
    };
    modelId?: string;
    topK?: number;
    maxRows?: number;
  }): AsyncGenerator<{ type: 'sql' | 'rows' | 'text' | 'error' | 'done'; data: unknown }> {
    const baseUrl = (apiClient.defaults.baseURL ?? '').replace(/\/$/, '');
    const res = await fetch(`${baseUrl}/api/knowledge-bases/db/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiClient.defaults.headers?.common as Record<string, string> | undefined),
      },
      body: JSON.stringify(params),
    });

    if (!res.ok || !res.body) {
      yield { type: 'error', data: `HTTP ${res.status}` };
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6)) as {
            type: 'sql' | 'rows' | 'text' | 'error' | 'done';
            data: unknown;
          };
          yield event;
          if (event.type === 'done' || event.type === 'error') return;
        } catch {
          // 파싱 실패 무시
        }
      }
    }
  },
};

export default knowledgeBaseApi;
